import React, { useMemo, useState } from 'react';
import { sendInteractiveReply } from '../services/MessageService';
import { formatWhatsAppText } from '../utils/whatsappFormatting';

export type LigInteractivePayload = {
  v: number;
  kind: string;
  whatsappMessageId: string;
  /** JID da conversa ao receber a mensagem (útil quando o contato só tem dígitos). */
  chatJId?: string;
  previewText?: string;
  headerText?: string;
  bodyText?: string;
  footerText?: string;
  listButtonText?: string;
  buttons?: Array<{
    kind: string;
    id?: string;
    displayText: string;
    nativeFlowName?: string;
    nativeFlowParamsJson?: string;
  }>;
  sections?: Array<{
    title?: string;
    rows: Array<{ id: string; title: string; description?: string }>;
  }>;
  nativeFlowMessageParamsJson?: string;
  nativeFlowMessageVersion?: number;
};

function parsePayload(raw: string | null | undefined): LigInteractivePayload | null {
  const s = (raw || '').trim();
  if (!s.startsWith('{')) return null;
  try {
    const p = JSON.parse(s) as LigInteractivePayload;
    if (!p || typeof p !== 'object' || p.v !== 1 || !p.whatsappMessageId) return null;
    return p;
  } catch {
    return null;
  }
}

export interface InteractiveMessageBubbleProps {
  /** JSON da mensagem (Conteudo) */
  content: string | null | undefined;
  isSent: boolean;
  recipientPhone: string;
  sectorId: number;
  userId: number;
  isAnonymous: boolean;
}

const InteractiveMessageBubble: React.FC<InteractiveMessageBubbleProps> = ({
  content,
  isSent,
  recipientPhone,
  sectorId,
  userId,
  isAnonymous
}) => {
  const payload = useMemo(() => parsePayload(content), [content]);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!payload) {
    return (
      <span className="lig-interactive-fallback">
        {formatWhatsAppText(content || '')}
      </span>
    );
  }

  const stanza = payload.whatsappMessageId;
  const showHeader = !!payload.headerText?.trim();
  const showBody = !!payload.bodyText?.trim();
  const showFooter = !!payload.footerText?.trim();

  const runReply = async (
    kind: 'buttons_response' | 'list_response' | 'native_flow_response',
    opts: {
      selectedButtonID?: string;
      selectedDisplayText?: string;
      listTitle?: string;
      listDescription?: string;
      listRowID?: string;
      nativeFlowName?: string;
      nativeFlowParamsJSON?: string;
      nativeFlowVersion?: number;
    },
    dedupeKey: string
  ) => {
    if (!recipientPhone || !stanza || isSent) return;
    setBusyKey(dedupeKey);
    setError(null);
    try {
      await sendInteractiveReply({
        recipientPhone,
        chatJId: payload.chatJId,
        sectorId,
        userId,
        isAnonymous,
        stanzaId: stanza,
        kind,
        selectedButtonId: opts.selectedButtonID,
        selectedDisplayText: opts.selectedDisplayText,
        listTitle: opts.listTitle,
        listDescription: opts.listDescription,
        listRowId: opts.listRowID,
        nativeFlowName: opts.nativeFlowName,
        nativeFlowParamsJson: opts.nativeFlowParamsJSON,
        nativeFlowVersion: opts.nativeFlowVersion
      });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = ax.response?.data?.message || (e instanceof Error ? e.message : null) || 'Erro ao enviar';
      setError(msg);
    } finally {
      setBusyKey(null);
    }
  };

  const renderButtons = () => {
    if (!payload.buttons?.length) return null;
    return (
      <div className="lig-interactive-actions">
        {payload.buttons.map((b, i) => {
          const key = `b-${i}-${b.kind}-${b.id || ''}-${b.nativeFlowName || ''}`;
          const loading = busyKey === key;
          const label = (b.displayText || b.nativeFlowName || 'Abrir').trim();
          return (
            <button
              key={key}
              type="button"
              className="lig-interactive-btn"
              disabled={loading || isSent}
              onClick={(e) => {
                e.stopPropagation();
                if (b.kind === 'native_flow') {
                  void runReply(
                    'native_flow_response',
                    {
                      selectedDisplayText: label,
                      nativeFlowName: b.nativeFlowName || label,
                      nativeFlowParamsJSON: b.nativeFlowParamsJson || '{}',
                      nativeFlowVersion:
                        payload.nativeFlowMessageVersion !== undefined && payload.nativeFlowMessageVersion !== null
                          ? Number(payload.nativeFlowMessageVersion)
                          : undefined
                    },
                    key
                  );
                } else {
                  void runReply(
                    'buttons_response',
                    {
                      selectedButtonID: b.id || '',
                      selectedDisplayText: label
                    },
                    key
                  );
                }
              }}
            >
              {loading ? '…' : label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderList = () => {
    if (!payload.sections?.length) return null;
    const openLabel = (payload.listButtonText || 'Ver opções').trim();
    return (
      <details className="lig-interactive-list">
        <summary className="lig-interactive-list-summary">{openLabel}</summary>
        {payload.sections.map((sec, si) => (
          <div key={`sec-${si}`} className="lig-interactive-section">
            {sec.title ? <div className="lig-interactive-section-title">{sec.title}</div> : null}
            <div className="lig-interactive-rows">
              {(sec.rows || []).map((row, ri) => {
                const key = `r-${si}-${ri}-${row.id}`;
                const loading = busyKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className="lig-interactive-rowbtn"
                    disabled={loading || isSent}
                    onClick={(e) => {
                      e.stopPropagation();
                      void runReply(
                        'list_response',
                        {
                          listTitle: row.title,
                          listDescription: row.description,
                          listRowID: row.id
                        },
                        key
                      );
                    }}
                  >
                    <span className="lig-interactive-row-title">{row.title}</span>
                    {row.description ? (
                      <span className="lig-interactive-row-desc">{row.description}</span>
                    ) : null}
                    {loading ? <span className="lig-interactive-row-loading">…</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </details>
    );
  };

  return (
    <div className="lig-interactive-card" onClick={(e) => e.stopPropagation()}>
      {showHeader ? <div className="lig-interactive-header">{payload.headerText}</div> : null}
      {showBody ? (
        <div className="lig-interactive-body">{formatWhatsAppText(payload.bodyText || '')}</div>
      ) : null}
      {payload.kind === 'list_message' ? renderList() : renderButtons()}
      {showFooter ? <div className="lig-interactive-footer">{payload.footerText}</div> : null}
      {error ? <div className="lig-interactive-error">{error}</div> : null}
      {isSent ? (
        <div className="lig-interactive-hint muted">Enviado pelo WhatsApp — apenas visualização</div>
      ) : null}
    </div>
  );
};

export default InteractiveMessageBubble;
